import { useEffect, useState, useRef, useCallback, type ChangeEvent } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Skeleton, App } from 'antd'
import { ArrowLeft, Mic, NotebookPen } from 'lucide-react'
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable'
import type { DragEndEvent } from '@dnd-kit/core'
import { v4 as uuidv4 } from 'uuid'
import { MindmapInterviewService } from '../../features/interview/services/mindmapInterviewService'
import { InterviewQuestionEditor } from '../../features/interview/components/builder'
import type { InterviewQuestion } from '@bandos/shared'
import {
  BuilderPage,
  BuilderContent,
  HeaderLeft,
  HeaderMeta,
  PageBrandLabel,
  MetaFields,
  MetaNameInput,
  MetaDescriptionInput,
  HeaderRight,
  SaveIndicator,
  RecordButton,
  QuestionList,
  AddQuestionButton,
  SkeletonWrapper,
} from './InterviewBuilderPageStyles'
import { PageHeader, BackButton } from '../../styles/pageHeaderStyles'

const DEBOUNCE_MS = 800

function ensureIds(questions: InterviewQuestion[]): InterviewQuestion[] {
  return questions.map((q) => (q.id ? q : { ...q, id: uuidv4() }))
}

export function InterviewBuilderPage() {
  const { mapId, nodeId } = useParams<{ mapId: string; nodeId: string }>()
  const navigate = useNavigate()
  const { notification } = App.useApp()

  const [questions, setQuestions] = useState<InterviewQuestion[]>([])
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [loading, setLoading] = useState(true)
  const [saveStatus, setSaveStatus] = useState<'saving' | 'saved' | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const metaDebounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => {
    if (!mapId || !nodeId) return
    MindmapInterviewService.get(mapId, nodeId)
      .then((res) => {
        if (res.error || !res.data) {
          notification.error({ title: 'Failed to load interview' })
          return
        }
        setQuestions(ensureIds(res.data.interview.questions))
        setName(res.data.interview.label)
        setDescription(res.data.interview.description ?? '')
      })
      .catch(() => notification.error({ title: 'Failed to load interview' }))
      .finally(() => setLoading(false))
  }, [mapId, nodeId, notification])

  const saveQuestions = useCallback(
    async (updated: InterviewQuestion[]) => {
      if (!mapId || !nodeId) return
      setSaveStatus('saving')
      const res = await MindmapInterviewService.update(mapId, nodeId, { questions: updated })
      setSaveStatus(res.error ? null : 'saved')
      if (res.error) notification.error({ title: 'Failed to save' })
    },
    [mapId, nodeId, notification]
  )

  const saveMeta = useCallback(
    async (updatedName: string, updatedDescription: string) => {
      if (!mapId || !nodeId) return
      setSaveStatus('saving')
      const res = await MindmapInterviewService.update(mapId, nodeId, {
        label: updatedName,
        description: updatedDescription || null,
      })
      setSaveStatus(res.error ? null : 'saved')
      if (res.error) notification.error({ title: 'Failed to save' })
    },
    [mapId, nodeId, notification]
  )

  const handleNameChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      setName(value)
      clearTimeout(metaDebounceRef.current)
      metaDebounceRef.current = setTimeout(() => void saveMeta(value, description), DEBOUNCE_MS)
    },
    [description, saveMeta]
  )

  const handleDescriptionChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value
      setDescription(value)
      e.target.style.height = 'auto'
      e.target.style.height = `${e.target.scrollHeight}px`
      clearTimeout(metaDebounceRef.current)
      metaDebounceRef.current = setTimeout(() => void saveMeta(name, value), DEBOUNCE_MS)
    },
    [name, saveMeta]
  )

  const handleChange = useCallback(
    (index: number, updated: InterviewQuestion) => {
      setQuestions((prev) => {
        const next = prev.map((q, i) => (i === index ? updated : q))
        clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => void saveQuestions(next), DEBOUNCE_MS)
        return next
      })
    },
    [saveQuestions]
  )

  const handleDelete = useCallback(
    (index: number) => {
      setQuestions((prev) => {
        const next = prev.filter((_, i) => i !== index)
        clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => void saveQuestions(next), DEBOUNCE_MS)
        return next
      })
    },
    [saveQuestions]
  )

  const handleAdd = useCallback(() => {
    setQuestions((prev) => [...prev, { id: uuidv4(), question: '' }])
  }, [])

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      if (!over || active.id === over.id) return
      setQuestions((prev) => {
        const oldIndex = prev.findIndex((q) => q.id === String(active.id))
        const newIndex = prev.findIndex((q) => q.id === String(over.id))
        if (oldIndex === -1 || newIndex === -1) return prev
        const next = arrayMove(prev, oldIndex, newIndex)
        clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => void saveQuestions(next), DEBOUNCE_MS)
        return next
      })
    },
    [saveQuestions]
  )

  const sensors = useSensors(useSensor(PointerSensor))

  // Clear pending saves on unmount to prevent stale API calls
  useEffect(() => {
    return () => {
      clearTimeout(debounceRef.current)
      clearTimeout(metaDebounceRef.current)
    }
  }, [])

  if (loading) {
    return (
      <SkeletonWrapper>
        <Skeleton active paragraph={{ rows: 6 }} />
      </SkeletonWrapper>
    )
  }

  return (
    <BuilderPage>
      <BuilderContent>
        <PageHeader>
          <HeaderLeft>
            <BackButton
              onClick={() => navigate(`/interview/${nodeId}/insights`, { replace: true })}
              title="Back"
            >
              <ArrowLeft size={18} />
            </BackButton>
            <HeaderMeta>
              <PageBrandLabel>
                <Mic size={13} />
                Interview Builder
              </PageBrandLabel>
              <MetaFields>
                <MetaNameInput
                  value={name}
                  onChange={handleNameChange}
                  placeholder="Interview name"
                  aria-label="Interview name"
                />
                <MetaDescriptionInput
                  value={description}
                  onChange={handleDescriptionChange}
                  placeholder="Optional description"
                  aria-label="Interview description"
                  rows={1}
                />
              </MetaFields>
            </HeaderMeta>
          </HeaderLeft>
          <HeaderRight>
            {saveStatus === 'saving' && <SaveIndicator>Saving…</SaveIndicator>}
            {saveStatus === 'saved' && <SaveIndicator>Saved ✓</SaveIndicator>}
            <RecordButton onClick={() => navigate(`/interview/${nodeId}/conduct`)}>
              <NotebookPen size={13} />
              Record interview
            </RecordButton>
          </HeaderRight>
        </PageHeader>

        <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
          <SortableContext
            items={questions.map((q) => q.id!)}
            strategy={verticalListSortingStrategy}
          >
            <QuestionList>
              {questions.map((q, i) => (
                <InterviewQuestionEditor
                  key={q.id}
                  id={q.id!}
                  question={q}
                  index={i}
                  onChange={(updated) => handleChange(i, updated)}
                  onDelete={() => handleDelete(i)}
                />
              ))}
            </QuestionList>
          </SortableContext>
        </DndContext>

        <AddQuestionButton onClick={handleAdd}>+ Add question</AddQuestionButton>
      </BuilderContent>
    </BuilderPage>
  )
}
